import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const PERIOD_PROFILES = {
  day: {
    label: '日',
    title: '今日快照',
    note: '更关注当日首响、当日长电话落地与敏感会话修正。',
    bias: { satisfaction: 0, response: 4, voip: -5, callDuration: -4, anomaly: 1, touchesScale: 0.3, positive: 0 },
    drift: [-3, -2, -1, 0, 1, 2, 3],
    times: ['09:06', '09:18', '10:12', '13:40', '18:28', '20:16'],
  },
  week: {
    label: '周',
    title: '本周表现',
    note: '适合看高频家长沟通、长电话覆盖与异常沟通走势。',
    bias: { satisfaction: 0, response: 1, voip: 0, callDuration: 0, anomaly: 0, touchesScale: 1, positive: 0 },
    drift: [-2, -1, 0, 1, 1, 2, 2],
    times: ['周一 09:06', '周一 09:18', '周二 10:12', '周三 13:40', '周四 18:28', '周五 20:16'],
  },
  fortnight: {
    label: '双周',
    title: '双周复盘',
    note: '更适合看家长情绪波动、跟进连续性和长电复盘效果。',
    bias: { satisfaction: 1, response: 0, voip: 2, callDuration: 2, anomaly: 0, touchesScale: 1.65, positive: 1 },
    drift: [-2, -1, 0, 1, 2, 2, 3],
    times: ['第1周 周一', '第1周 周三', '第1周 周五', '第2周 周二', '第2周 周四', '第2周 周六'],
  },
  month: {
    label: '月',
    title: '月度经营',
    note: '适合看分组经营质量、家长反馈率和报名推进结果。',
    bias: { satisfaction: 1, response: -1, voip: 3, callDuration: 3, anomaly: -1, touchesScale: 2.7, positive: 2 },
    drift: [-1, 0, 1, 2, 3, 3, 4],
    times: ['4月03日', '4月06日', '4月09日', '4月13日', '4月16日', '4月18日'],
  },
  quarter: {
    label: '季度',
    title: '季度趋势',
    note: '用于观察指导老师结构、分组差异与家长长期满意度。',
    bias: { satisfaction: 2, response: -2, voip: 4, callDuration: 4, anomaly: -1, touchesScale: 4.2, positive: 3 },
    drift: [-1, 0, 1, 2, 3, 4, 5],
    times: ['Q2 第1周', 'Q2 第3周', 'Q2 第5周', 'Q2 第7周', 'Q2 第9周', 'Q2 第11周'],
  },
}

const GROUPS = [
  {
    id: 'lead-intake',
    name: '新线索首触组',
    teacherCount: 25,
    color: '#769eb0',
    soft: 'rgba(118, 158, 176, 0.16)',
    segment: '新入池家长',
    description: '负责家长首次破冰、需求摸底与首通长电话。',
    specialties: ['首触摸底', '首通长电', '试听预约'],
    shifts: ['日间高频', '晚间深聊', '周末预约'],
    stages: ['新线索摸底', '试听预约', '首通后观察'],
    concerns: ['孩子专注力', '试听是否合适', '课程时间冲突', '是否需要先测评'],
    materials: ['试听确认单', '家长常见问题页', '孩子测评说明'],
    nextActions: ['预约首次 28 分钟 语音', '补发试听前说明', '确认家长方便的晚间沟通时段'],
    base: { satisfaction: 86, response: 91, voip: 83, callDuration: 29, anomaly: 7, touches: 23, positive: 84 },
  },
  {
    id: 'trial-follow',
    name: '试听转化组',
    teacherCount: 25,
    color: '#8baa7c',
    soft: 'rgba(139, 170, 124, 0.16)',
    segment: '试听后家长',
    description: '围绕试听反馈展开长电话复盘，推动家长理解课程适配度。',
    specialties: ['试听复盘', '家长答疑', '班型推荐'],
    shifts: ['日间高频', '晚间深聊', '周末预约'],
    stages: ['试听后跟进', '班型推荐', '家长答疑'],
    concerns: ['试听效果好不好', '课程是否匹配孩子', '班型怎么选', '要不要先上体验课'],
    materials: ['试听反馈单', '班型对比页', '课程适配说明'],
    nextActions: ['完成一次 30 分钟试听复盘 语音', '把试听反馈与班型推荐同步给家长', '确认家长对课程节奏的接受度'],
    base: { satisfaction: 88, response: 88, voip: 85, callDuration: 30, anomaly: 6, touches: 22, positive: 86 },
  },
  {
    id: 'growth-plan',
    name: '成长方案组',
    teacherCount: 25,
    color: '#b08968',
    soft: 'rgba(176, 137, 104, 0.16)',
    segment: '方案深聊家长',
    description: '针对已有初步意向的家长，深入沟通孩子成长路径与课程价值。',
    specialties: ['成长规划', '课程价值传递', '异议处理'],
    shifts: ['日间高频', '晚间深聊', '重点家庭专席'],
    stages: ['成长方案沟通', '课程价值深聊', '异议处理'],
    concerns: ['课程费用是否值得', '孩子能不能坚持', '和其他机构比有什么不同', '家长对效果有疑虑'],
    materials: ['成长方案页', '课程价值对比', '家长案例集'],
    nextActions: ['围绕成长方案做一次 35 分钟深聊 语音', '把课程价值和家长核心顾虑对应讲清楚', '同步一份简明成长规划给家长确认'],   
    base: { satisfaction: 87, response: 86, voip: 87, callDuration: 34, anomaly: 8, touches: 21, positive: 85 },
  },
  {
    id: 'closing-push',
    name: '临门冲刺组',
    teacherCount: 25,
    color: '#d3a06c',
    soft: 'rgba(211, 160, 108, 0.16)',
    segment: '临门报名家长',
    description: '重点处理家长临门犹豫、排课确认与支付前问题。',
    specialties: ['报名临门', '排课确认', '预算异议处理'],
    shifts: ['日间高频', '晚间深聊', '周末冲刺'],
    stages: ['报名临门', '支付前确认', '排课落单'],
    concerns: ['报名费用', '孩子是否坚持', '排课能否协调', '顾虑是否已解决'],
    materials: ['报名确认页', '课表方案', '家长顾虑清单'],
    nextActions: ['补一次 26 分钟异议处理 语音', '把排课和费用拆开讲清楚', '确认家长最后一个犹豫点'],
    base: { satisfaction: 85, response: 89, voip: 88, callDuration: 31, anomaly: 9, touches: 24, positive: 85 },
  },
  {
    id: 'renewal-care',
    name: '在读续费组',
    teacherCount: 25,
    color: '#75a79b',
    soft: 'rgba(117, 167, 155, 0.16)',
    segment: '在读家庭',
    description: '关注续费前复盘、阶段反馈和孩子后续学习节奏。',
    specialties: ['续费复盘', '阶段反馈', '学习节奏维护'],
    shifts: ['日间高频', '晚间深聊', '重点家庭专席'],
    stages: ['阶段复盘', '续费意向', '假期规划'],
    concerns: ['孩子进步是否明显', '是否续费', '假期班如何安排', '老师反馈够不够清晰'],
    materials: ['阶段反馈单', '续费建议页', '假期规划表'],
    nextActions: ['安排续费前 35 分钟复盘 语音', '提炼孩子阶段变化给家长确认', '把续费方案和目标重新挂钩'],
    base: { satisfaction: 90, response: 85, voip: 90, callDuration: 37, anomaly: 6, touches: 22, positive: 91 },
  },
  {
    id: 'wake-up',
    name: '沉默唤醒组',
    teacherCount: 25,
    color: '#c98f8f',
    soft: 'rgba(201, 143, 143, 0.16)',
    segment: '沉默 / 失联家庭',
    description: '针对沉默家长与情绪波动家庭进行重新连接和唤醒。',
    specialties: ['沉默唤醒', '情绪修复', '重连长电'],
    shifts: ['晚间深聊', '周末预约', '重点家庭专席'],
    stages: ['沉默唤醒', '情绪修复', '二次激活'],
    concerns: ['家长长期未回', '孩子兴趣下降', '家长情绪偏谨慎', '前次沟通体验不佳'],
    materials: ['重连话术页', '阶段回顾单', '轻量重启方案'],
    nextActions: ['以关怀为主完成一次重连 语音', '减少连环追问，先确认家长状态', '若 72 小时无反馈则转低频培育'],
    base: { satisfaction: 80, response: 79, voip: 85, callDuration: 33, anomaly: 12, touches: 19, positive: 78 },
  },
  {
    id: 'young-start',
    name: '低龄启蒙组',
    teacherCount: 25,
    color: '#d9a47f',
    soft: 'rgba(217, 164, 127, 0.16)',
    segment: '低龄启蒙家庭',
    description: '面向低龄家庭，重点处理启蒙阶段的适配、节奏和亲子配合。',
    specialties: ['低龄启蒙', '亲子沟通', '启蒙试听'],
    shifts: ['日间高频', '晚间深聊', '周末预约'],
    stages: ['启蒙评估', '试听预约', '启蒙方案确认'],
    concerns: ['孩子是否坐得住', '启蒙课有没有必要', '家长是否需要陪练', '低龄课程节奏会不会太快'],
    materials: ['启蒙课程页', '亲子配合建议', '低龄试听说明'],
    nextActions: ['安排一次 30 分钟启蒙说明 语音', '先让家长确认孩子当前习惯', '补发一页低龄课程节奏说明'],
    base: { satisfaction: 87, response: 90, voip: 86, callDuration: 31, anomaly: 7, touches: 24, positive: 86 },
  },
  {
    id: 'subject-boost',
    name: '学科提升组',
    teacherCount: 25,
    color: '#7ca8d3',
    soft: 'rgba(124, 168, 211, 0.16)',
    segment: '学科提升家庭',
    description: '围绕成绩提升、短板突破和学习习惯建立展开连续长电话沟通。',
    specialties: ['学科规划', '短板提升', '学习习惯'],
    shifts: ['日间高频', '晚间深聊', '重点家庭专席'],
    stages: ['学科诊断', '提升方案', '阶段复盘'],
    concerns: ['成绩波动明显', '孩子偏科严重', '家长怕课程没效果', '学习习惯难坚持'],
    materials: ['学科诊断页', '阶段提升方案', '习惯打卡建议'],
    nextActions: ['安排一次 38 分钟学科规划 语音', '围绕孩子短板给到 3 个可执行动作', '把课程价值和孩子近况直接挂钩'],
    base: { satisfaction: 88, response: 85, voip: 90, callDuration: 38, anomaly: 8, touches: 26, positive: 88 },
  },
  {
    id: 'weekend-plan',
    name: '周末冲刺组',
    teacherCount: 25,
    color: '#c7b07d',
    soft: 'rgba(199, 176, 125, 0.16)',
    segment: '周末高频家庭',
    description: '集中处理周末试听、补课安排和临门报名沟通。',
    specialties: ['周末预约', '冲刺转化', '排课协调'],
    shifts: ['周末预约', '晚间深聊', '周末冲刺'],
    stages: ['周末预约', '排课协调', '报名确认'],
    concerns: ['周末时间冲突', '试听后能否及时排课', '报名节奏太赶', '家长需要更明确安排'],
    materials: ['周末排期页', '报名确认单', '排课沟通模板'],
    nextActions: ['锁定周末前一次确认 语音', '提前把排课方案讲清楚', '避免家长在临门前还在反复确认时间'], 
    base: { satisfaction: 84, response: 90, voip: 86, callDuration: 30, anomaly: 10, touches: 25, positive: 84 },
  },
  {
    id: 'vip-care',
    name: '重点家庭服务组',
    teacherCount: 25,
    color: '#a98fb7',
    soft: 'rgba(169, 143, 183, 0.16)',
    segment: '重点家庭',
    description: '服务高关注、高客单和需要连续深聊的重点家庭。',
    specialties: ['重点服务', '长期规划', '家长深聊'],
    shifts: ['重点家庭专席', '晚间深聊', '周末预约'],
    stages: ['重点深聊', '长期规划', '阶段陪跑'],
    concerns: ['家长期望值很高', '需要更细致反馈', '孩子规划更长期', '家长决策更谨慎'],
    materials: ['重点家庭沟通纪要', '长期规划单', '阶段反馈摘要'],
    nextActions: ['安排一次 42 分钟重点家庭深聊 VOIP', '把阶段反馈和长期规划并行讲清楚', '会后补一版精简纪要给家长确认'],
    base: { satisfaction: 91, response: 84, voip: 93, callDuration: 42, anomaly: 6, touches: 24, positive: 92 },
  },
]

const CITIES = ['上海', '深圳', '北京', '杭州', '广州', '成都', '苏州', '南京', '武汉', '重庆']
const CAMPUSES = ['虹桥校区', '南山校区', '望京校区', '滨江校区', '天河校区', '高新校区', '园区校区', '河西校区', '光谷校区', '渝北校区']
const SURNAMES = ['陈', '林', '周', '许', '王', '李', '赵', '黄', '徐', '何', '孙', '张', '吴', '郑', '谢', '宋', '冯', '曹', '袁', '邓']
const NAME_A = ['思', '若', '知', '景', '亦', '书', '安', '言', '星', '念', '舒', '晨', '可', '映', '清', '乔', '嘉', '子', '向', '予']
const NAME_B = ['宁', '远', '岚', '悦', '杭', '舟', '一', '禾', '川', '林', '夏', '朗', '彤', '溪', '南', '衡', '诺', '潇', '澄', '可']
const CHILD_NAMES = ['沐沐', '安安', '一诺', '糖糖', '言言', '果果', '辰辰', '朵朵', '皓皓', '可可', '西西', '宸宸', '乐乐', '小满', '初初', '晨晨', '诺诺', '小北', '悠悠', '星星']
const RELATIONS = ['妈妈', '爸爸', '家长']
const GRADES = ['幼儿园中班', '幼儿园大班', '一年级', '二年级', '三年级', '四年级', '五年级', '六年级', '初一', '初二']

function hashString(text) {
  let hash = 2166136261
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function mulberry32(seed) {
  return function next() {
    let value = seed += 0x6D2B79F5
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function average(values) {
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
}

function pick(list, rng) {
  return list[Math.floor(rng() * list.length)]
}

function range(min, max, rng) {
  return min + Math.floor(rng() * (max - min + 1))
}

function weightedLevel(rng) {
  const roll = rng()
  if (roll < 0.18) return 'S'
  if (roll < 0.69) return 'A'
  return 'B'
}

function teacherName(index, groupIndex) {
  return `${SURNAMES[(index + groupIndex * 3) % SURNAMES.length]}${NAME_A[(index * 2 + groupIndex) % NAME_A.length]}${NAME_B[(index * 3 + groupIndex * 2) % NAME_B.length]}`
}

function trendFrom(base, rng) {
  const points = []
  let current = base - range(4, 8, rng)
  for (let index = 0; index < 7; index += 1) {
    current += range(-1, 3, rng)
    points.push(clamp(current, 60, 99))
  }
  return points
}

function buildCustomer(group, advisor, familyIndex, rng) {
  const childName = pick(CHILD_NAMES, rng)
  const relation = pick(RELATIONS, rng)
  const grade = pick(GRADES, rng)
  const city = pick(CITIES, rng)
  const stage = pick(group.stages, rng)
  const concern = pick(group.concerns, rng)
  const material = pick(group.materials, rng)
  const nextAction = pick(group.nextActions, rng)
  const baseSatisfaction = clamp(group.base.satisfaction + range(-8, 7, rng), 68, 98)
  const baseResponse = clamp(group.base.response + range(-10, 7, rng), 70, 99)
  const baseVoip = clamp(group.base.voip + range(-9, 7, rng), 64, 98)
  const baseDuration = clamp(group.base.callDuration + range(-9, 10, rng), 18, 58)
  const baseAnomaly = clamp(group.base.anomaly + range(-4, 6, rng), 2, 19)
  const baseTouches = clamp(group.base.touches + range(-8, 10, rng), 8, 48)
  const basePositive = clamp(group.base.positive + range(-8, 7, rng), 65, 97)

  return {
    id: `${advisor.id}-family-${familyIndex + 1}`,
    familyName: `${childName}${relation}`,
    relation,
    childName,
    childLabel: `${childName} · ${grade}`,
    city,
    stage,
    concern,
    material,
    nextAction,
    note: `${relation}最近围绕"${concern}"与指导老师保持高频沟通，当前重点是${stage}。`,
    base: {
      satisfaction: baseSatisfaction,
      response: baseResponse,
      voip: baseVoip,
      callDuration: baseDuration,
      anomaly: baseAnomaly,
      touches: baseTouches,
      positive: basePositive,
      trend: trendFrom(baseSatisfaction, rng),
    },
  }
}

function aggregateAdvisorBase(customers, level, rng) {
  const boost = level === 'S' ? 2 : level === 'A' ? 0 : -2
  const fields = ['satisfaction', 'response', 'voip', 'callDuration', 'anomaly', 'touches', 'positive']
  const base = {}

  fields.forEach((field) => {
    const values = customers.map((customer) => customer.base[field])
    base[field] = average(values)
  })

  const trend = customers[0].base.trend.map((_, index) => average(customers.map((customer) => customer.base.trend[index])))

  return {
    satisfaction: clamp(base.satisfaction + boost, 70, 98),
    response: clamp(base.response + boost, 70, 99),
    voip: clamp(base.voip + (boost > 0 ? 1 : 0), 64, 98),
    callDuration: clamp(base.callDuration + (boost > 0 ? 1 : 0), 18, 60),
    anomaly: clamp(base.anomaly + (boost < 0 ? 1 : boost > 0 ? -1 : 0), 2, 20),
    touches: clamp(base.touches + range(-1, 2, rng), 8, 52),
    positive: clamp(base.positive + boost, 65, 98),
    trend,
  }
}

function buildAdvisors() {
  const advisors = []

  GROUPS.forEach((group, groupIndex) => {
    for (let teacherIndex = 0; teacherIndex < group.teacherCount; teacherIndex += 1) {
      const rng = mulberry32(hashString(`${group.id}-${teacherIndex}`))
      const level = weightedLevel(rng)
      const shift = pick(group.shifts, rng)
      const specialty = pick(group.specialties, rng)
      const familyCount = range(8, 16, rng)
      const advisor = {
        id: `${group.id}-teacher-${teacherIndex + 1}`,
        name: teacherName(teacherIndex, groupIndex),
        groupId: group.id,
        groupName: group.name,
        level,
        shift,
        specialty,
        tenure: range(1, 6, rng),
        campus: `${pick(CITIES, rng)} · ${pick(CAMPUSES, rng)}`,
      }

      advisor.customers = Array.from({ length: familyCount }, (_, familyIndex) => buildCustomer(group, advisor, familyIndex, rng))
      advisor.base = aggregateAdvisorBase(advisor.customers, level, rng)
      advisor.note = `${advisor.level} 级指导老师，当前以${advisor.shift}节奏负责${advisor.customers.length}个在沟通家庭。`
      advisors.push(advisor)
    }
  })

  return advisors
}

const advisors = buildAdvisors()

const VALID_PERIODS = Object.keys(PERIOD_PROFILES)

function getRouteValue(route, key) {
  const paramValue = route.params[key]
  if (typeof paramValue === 'string' && paramValue.length) return paramValue
  if (Array.isArray(paramValue) && typeof paramValue[0] === 'string' && paramValue[0].length) return paramValue[0]
  const queryValue = route.query[key]
  if (typeof queryValue === 'string' && queryValue.length) return queryValue
  if (Array.isArray(queryValue) && typeof queryValue[0] === 'string' && queryValue[0].length) return queryValue[0]
  return ''
}

function percent(part, total) {
  return total ? Math.round((part / total) * 100) : 0
}

function touchThreshold(periodKey) {
  return { day: 5, week: 22, fortnight: 34, month: 56, quarter: 88 }[periodKey]
}

function variance(seed, shift, spread) {
  const raw = (seed >> shift) & 15
  return raw - spread
}

function deriveSnapshot(base, periodValue, key) {
  const profile = PERIOD_PROFILES[periodValue]
  const seed = hashString(`${periodValue}:${key}`)

  return {
    satisfaction: clamp(base.satisfaction + profile.bias.satisfaction + variance(seed, 1, 5), 65, 99),
    response: clamp(base.response + profile.bias.response + variance(seed, 3, 5), 68, 99),
    voip: clamp(base.voip + profile.bias.voip + variance(seed, 5, 4), 58, 99),
    callDuration: clamp(base.callDuration + profile.bias.callDuration + variance(seed, 7, 4), 16, 64),
    anomaly: clamp(base.anomaly + profile.bias.anomaly + variance(seed, 9, 3), 1, 22),
    touches: Math.max(2, Math.round((base.touches + variance(seed, 11, 4)) * profile.bias.touchesScale)),
    positive: clamp(base.positive + profile.bias.positive + variance(seed, 13, 4), 60, 99),
    trend: base.trend.map((point, index) => clamp(point + profile.drift[index] + variance(seed, index + 2, 2), 58, 99)),
  }
}

function toneFrom(snapshot) {
  if (snapshot.satisfaction >= 89 && snapshot.voip >= 87 && snapshot.anomaly <= 8) {
    return { label: '稳定', color: 'var(--good)', soft: 'var(--good-soft)' }
  }
  if (snapshot.anomaly >= 13 || snapshot.response <= 78 || snapshot.satisfaction <= 80) {
    return { label: '异常', color: 'var(--danger)', soft: 'var(--danger-soft)' }
  }
  return { label: '关注', color: 'var(--warn)', soft: 'var(--warn-soft)' }
}

function trendText(points) {
  const delta = points[points.length - 1] - points[points.length - 2]
  if (delta >= 2) return '上扬'
  if (delta <= -2) return '回落'
  return '走稳'
}

function stageKey(stage) {
  if (stage.includes('新线索') || stage.includes('试听预约')) return '新线索触达'
  if (stage.includes('试听') || stage.includes('反馈')) return '试听后跟进'
  if (stage.includes('成长') || stage.includes('方案')) return '成长方案深聊'
  if (stage.includes('报名') || stage.includes('支付')) return '报名临门'
  if (stage.includes('续费') || stage.includes('假期')) return '在读续费'
  return '沉默唤醒'
}

function recommendation(advisor, family) {
  if (family.snapshot.anomaly >= 13) {
    return `先由组长陪同复盘 ${family.familyName} 最近两次长电话，降低敏感时段连续追问，再恢复正常推进。`
  }
  if (family.snapshot.voip < 82) {
    return `把 ${family.familyName} 当前的关键问题从文字沟通切到更完整的 VOIP 深聊，避免反复碎片化解释。`
  }
  if (family.snapshot.response < 84) {
    return `缩短 ${advisor.name} 对 ${family.familyName} 的首响间隔，先给到明确回应，再补详细说明。`
  }
  return `继续保持当前长电话节奏，把这类家长沟通路径沉淀为 ${advisor.groupName} 的标准样板。`
}

export function useControlTowerData() {
  const route = useRoute()
  const router = useRouter()

  const period = computed(() => {
    const raw = getRouteValue(route, 'period')
    return VALID_PERIODS.includes(raw) ? raw : 'week'
  })

  const groupId = computed(() => getRouteValue(route, 'group') || GROUPS[0].id)
  const advisorId = computed(() => getRouteValue(route, 'advisor') || '')
  const familyId = computed(() => getRouteValue(route, 'family') || '')

  const periodProfiles = PERIOD_PROFILES

  const model = computed(() => {
    const allTeachers = advisors.map((advisor) => {
      const customers = advisor.customers
        .map((customer) => {
          const snapshot = deriveSnapshot(customer.base, period.value, customer.id)
          return { ...customer, snapshot, tone: toneFrom(snapshot) }
        })
        .sort((left, right) => {
          if (right.snapshot.anomaly !== left.snapshot.anomaly) return right.snapshot.anomaly - left.snapshot.anomaly
          return left.snapshot.satisfaction - right.snapshot.satisfaction
        })

      const snapshot = deriveSnapshot(advisor.base, period.value, advisor.id)
      const riskFamilies = customers.filter((customer) => customer.snapshot.anomaly >= 13).length
      const highFreqFamilies = customers.filter((customer) => customer.snapshot.touches >= touchThreshold(period.value)).length

      return { ...advisor, customers, snapshot, tone: toneFrom(snapshot), riskFamilies, highFreqFamilies }
    })

    const groupModels = GROUPS.map((group) => {
      const teachers = allTeachers
        .filter((teacher) => teacher.groupId === group.id)
        .sort((left, right) => {
          if (right.riskFamilies !== left.riskFamilies) return right.riskFamilies - left.riskFamilies
          return left.snapshot.satisfaction - right.snapshot.satisfaction
        })

      const families = teachers.flatMap((teacher) => teacher.customers)
      const riskFamilies = families.filter((family) => family.snapshot.anomaly >= 13).length
      const highFreqFamilies = families.filter((family) => family.snapshot.touches >= touchThreshold(period.value)).length
      const levelCount = teachers.reduce((accumulator, teacher) => {
        accumulator[teacher.level] = (accumulator[teacher.level] ?? 0) + 1
        return accumulator
      }, {})
      const shiftCount = teachers.reduce((accumulator, teacher) => {
        accumulator[teacher.shift] = (accumulator[teacher.shift] ?? 0) + 1
        return accumulator
      }, {})
      const specialtyCount = teachers.reduce((accumulator, teacher) => {
        accumulator[teacher.specialty] = (accumulator[teacher.specialty] ?? 0) + 1
        return accumulator
      }, {})

      return {
        ...group,
        teachers,
        families,
        summary: {
          teacherCount: teachers.length,
          familyCount: families.length,
          satisfaction: average(teachers.map((teacher) => teacher.snapshot.satisfaction)),
          voip: average(teachers.map((teacher) => teacher.snapshot.voip)),
          callDuration: average(teachers.map((teacher) => teacher.snapshot.callDuration)),
          positive: average(teachers.map((teacher) => teacher.snapshot.positive)),
          riskFamilies,
          highFreqFamilies,
          riskRate: percent(riskFamilies, families.length),
          saShare: percent((levelCount.S ?? 0) + (levelCount.A ?? 0), teachers.length),
          levelCount,
          shiftCount,
          specialtyCount,
        },
      }
    })

    const selectedGroup = groupModels.find((group) => group.id === groupId.value) ?? groupModels[0]
    const selectedAdvisor = selectedGroup.teachers.find((teacher) => teacher.id === advisorId.value) ?? selectedGroup.teachers[0]
    const selectedFamily = selectedAdvisor.customers.find((family) => family.id === familyId.value) ?? selectedAdvisor.customers[0]
    const allFamilies = groupModels.flatMap((group) => group.families)

    return {
      profile: PERIOD_PROFILES[period.value],
      groups: groupModels,
      selectedGroup,
      selectedAdvisor,
      selectedFamily,
      summary: {
        teacherCount: allTeachers.length,
        familyCount: allFamilies.length,
        satisfaction: average(allTeachers.map((teacher) => teacher.snapshot.satisfaction)),
        voip: average(allTeachers.map((teacher) => teacher.snapshot.voip)),
        callDuration: average(allTeachers.map((teacher) => teacher.snapshot.callDuration)),
        positive: average(allTeachers.map((teacher) => teacher.snapshot.positive)),
        riskFamilies: allFamilies.filter((family) => family.snapshot.anomaly >= 13).length,
        highFreqFamilies: allFamilies.filter((family) => family.snapshot.touches >= touchThreshold(period.value)).length,
      },
    }
  })

  const summaryCards = computed(() => {
    const m = model.value.summary
    return [
      { label: '指导师人数', value: `${m.teacherCount}`, note: '活跃服务中', tone: 'var(--brand)' },
      { label: '分配数据量', value: `${m.familyCount}`, note: '本周期分配', tone: 'var(--brand)' },
      { label: '整体触达率', value: `${m.satisfaction}%`, note: '家长综合触达', tone: 'var(--good)' },
      { label: '通话触达率', value: `${m.voip}%`, note: 'VOIP 通话覆盖', tone: 'var(--good)' },
      { label: '平均通话时长', value: `${m.callDuration} 分钟`, note: '深聊时长', tone: 'var(--warn)' },
      { label: '异常违规率', value: `${m.riskFamilies}`, note: `高频 ${m.highFreqFamilies}`, tone: 'var(--danger)' },
    ]
  })

  const topGroup = computed(() => {
    return [...model.value.groups].sort((left, right) => right.summary.satisfaction - left.summary.satisfaction)[0]
  })

  const watchFamilies = computed(() => {
    return model.value.groups
      .flatMap((item) => item.families.map((familyItem) => ({ ...familyItem, groupId: item.id, groupName: item.name })))
      .sort((left, right) => right.snapshot.anomaly - left.snapshot.anomaly)
      .slice(0, 3)
  })

  const topAdvisors = computed(() => {
    const allTeachers = model.value.groups.flatMap(g => g.teachers)
    return [...allTeachers]
      .sort((a, b) => b.snapshot.voip - a.snapshot.voip)
      .slice(0, 5)
      .map(t => ({
        name: t.name,
        groupName: t.groupName,
        customerCount: t.customers.length,
        voipRate: t.snapshot.voip,
      }))
  })

  const groupRanking = computed(() => {
    return [...model.value.groups]
      .sort((a, b) => a.summary.satisfaction - b.summary.satisfaction)
      .map(g => ({
        name: g.name,
        leaderName: g.teachers[0]?.name || '-',
        familyCount: g.families.length,
        satisfaction: g.summary.satisfaction,
        anomalyRate: g.summary.riskRate,
      }))
  })

  const customerProfile = computed(() => {
    const allFamilies = model.value.groups.flatMap(g => g.families)
    const gradeCount = {}
    const sourceCount = { '企微': 0, '转介绍': 0, '线上投放': 0, '活动报名': 0, '自然到访': 0 }
    const sources = Object.keys(sourceCount)
    allFamilies.forEach(f => {
      const grade = f.grade || '未知'
      gradeCount[grade] = (gradeCount[grade] || 0) + 1
      const src = sources[Math.abs(hashString(f.id)) % sources.length]
      sourceCount[src]++
    })
    const total = allFamilies.length || 1
    return {
      gradeDistribution: Object.entries(gradeCount).map(([name, count]) => ({ name, count, rate: Math.round(count / total * 100) })),
      sourceDistribution: Object.entries(sourceCount).map(([name, count]) => ({ name, count, rate: Math.round(count / total * 100) })),
    }
  })

  const familySignals = computed(() => {
    const family = model.value.selectedFamily
    return [
      { label: '首响节奏', value: family.snapshot.response, note: '家长发起咨询后是否获得及时回应。' },
      { label: 'VOIP 覆盖', value: family.snapshot.voip, note: '复杂问题是否通过长电话而非碎片文字解决。' },
      { label: '家长反馈', value: family.snapshot.positive, note: '反映家长对当前沟通是否认可。' },
      { label: '异常风险', value: clamp(family.snapshot.anomaly * 5, 10, 100), note: '数值越高，越需要复核表达和节奏。' },
    ]
  })

  const timelineItems = computed(() => {
    const { selectedAdvisor: advisor, selectedFamily: family, profile } = model.value
    const riskTone = family.tone

    return [
      {
        time: profile.times[0],
        title: '家长主动发起咨询',
        channel: '企微会话',
        tone: 'var(--brand)',
        desc: `${family.familyName} 围绕"${family.concern}"主动咨询，当前关注孩子 ${family.childLabel} 是否适合进入下一阶段。`,
        meta: [`城市：${family.city}`, `阶段：${family.stage}`],
      },
      {
        time: profile.times[1],
        title: '指导老师完成首响',
        channel: '首响监管',
        tone: riskTone.color,
        desc: `${advisor.name} 在当前周期内保持 ${family.snapshot.response}% 的首响达标率，并快速确认家长关注重点。`,
        meta: [`指导老师：${advisor.name}`, `趋势：${trendText(family.snapshot.trend)}`],
      },
      {
        time: profile.times[2],
        title: '完成长电话 VOIP 深聊',
        channel: 'VOIP 主沟通',
        tone: 'var(--good)',
        desc: `围绕 ${family.concern} 完成 ${family.snapshot.callDuration} 分钟 VOIP，重点解释孩子当前阶段、课程适配和家长顾虑。`,
        meta: [`VOIP 覆盖：${family.snapshot.voip}%`, `沟通频次：${family.snapshot.touches}`],
      },
      {
        time: profile.times[3],
        title: '发送资料与复盘摘要',
        channel: '资料跟进',
        tone: 'var(--warn)',
        desc: `指导老师发送 ${family.material}，并将长电话中的关键信息拆成家长更容易确认的 2 至 3 个动作。`,
        meta: [`资料：${family.material}`, `孩子信息：${family.childLabel}`],
      },
      {
        time: profile.times[4],
        title: family.snapshot.anomaly >= 13 ? '系统触发异常沟通提醒' : '系统判定沟通稳定',
        channel: family.snapshot.anomaly >= 13 ? '风险预警' : '巡检结果',
        tone: family.snapshot.anomaly >= 13 ? 'var(--danger)' : 'var(--good)',
        desc: family.snapshot.anomaly >= 13
          ? `检测到 ${family.familyName} 最近存在情绪波动或连续追问特征，建议由组长抽样复核会话。`
          : `当前未发现明显异常沟通模式，家长反馈率保持在 ${family.snapshot.positive}% 左右。`,
        meta: [`异常指数：${family.snapshot.anomaly}`, `反馈率：${family.snapshot.positive}%`],
      },
      {
        time: profile.times[5],
        title: '形成下一步动作',
        channel: '协作落地',
        tone: 'var(--brand-strong)',
        desc: recommendation(advisor, family),
        meta: [`下一步：${family.nextAction}`, '管理建议：围绕长电话继续推进'],
      },
    ]
  })

  const classificationSections = computed(() => {
    const group = model.value.selectedGroup
    const sections = [
      { title: '老师层级', values: group.summary.levelCount },
      { title: '班次分布', values: group.summary.shiftCount },
      { title: '专长分布', values: group.summary.specialtyCount },
    ]
    return sections.map((section) => {
      const rows = Object.entries(section.values).sort((left, right) => right[1] - left[1])
      const max = Math.max(...rows.map((row) => row[1]))
      return { title: section.title, max, rows }
    })
  })

  const actionItems = computed(() => {
    const advisor = model.value.selectedAdvisor
    const family = model.value.selectedFamily
    return [
      { role: '管理者', title: '锁定会中讲解对象', desc: `先讲 ${model.value.selectedGroup.name}，再讲 ${advisor.name} 与 ${family.familyName} 的长电话节奏和风险点。`, tag: '用于经营会' },
      { role: '组长', title: '抽样复核关键通话', desc: `优先复核 ${family.familyName} 最近两次 VOIP，检查是否存在重复追问或表达过载。`, tag: '24 小时内' },
      { role: '指导老师', title: '完成下一次跟进', desc: family.nextAction, tag: `${family.snapshot.callDuration} 分钟长电` },
      { role: '运营支持', title: '补充资料与话术', desc: `围绕 ${family.concern} 补充 ${family.material}，同步提供更适合家长理解的精简说明。`, tag: '材料包支持' },
    ]
  })

  const stageRows = computed(() => {
    const stageCount = model.value.selectedGroup.families.reduce((accumulator, family) => {
      const key = stageKey(family.stage)
      accumulator[key] = (accumulator[key] ?? 0) + 1
      return accumulator
    }, {})
    return Object.entries(stageCount).sort((left, right) => right[1] - left[1])
  })

  const stageMax = computed(() => {
    return Math.max(...stageRows.value.map((row) => row[1]))
  })

  function navigateQuery(params) {
    const merged = {
      period: period.value,
      group: groupId.value,
      advisor: advisorId.value,
      family: familyId.value,
      ...params,
    }
    const query = {}
    if (merged.period) query.period = merged.period
    if (merged.group) query.group = merged.group
    if (merged.advisor) query.advisor = merged.advisor
    if (merged.family) query.family = merged.family
    router.replace({ name: 'ControlTower', query })
  }

  function setPeriod(key) {
    navigateQuery({ period: key })
  }

  function selectGroup(id) {
    navigateQuery({ group: id, advisor: '', family: '' })
  }

  function selectAdvisor(id) {
    navigateQuery({ advisor: id, family: '' })
  }

  function selectFamily(id) {
    navigateQuery({ family: id })
  }

  function selectWatchFamily(item) {
    const targetGroup = model.value.groups.find((groupItem) => groupItem.id === item.groupId)
    const targetTeacher = targetGroup.teachers.find((teacher) => teacher.customers.some((familyItem) => familyItem.id === item.id))
    navigateQuery({ group: targetGroup.id, advisor: targetTeacher.id, family: item.id })
  }

  return {
    period,
    groupId,
    advisorId,
    familyId,
    periodProfiles,
    model,
    summaryCards,
    topGroup,
    watchFamilies,
    topAdvisors,
    groupRanking,
    customerProfile,
    familySignals,
    timelineItems,
    classificationSections,
    actionItems,
    stageRows,
    stageMax,
    percent,
    recommendation,
    setPeriod,
    selectGroup,
    selectAdvisor,
    selectFamily,
    selectWatchFamily,
  }
}
